import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import type { PaigeDestinationsBridge } from './paige.global';
import type { PaigeCcWaiting } from './product-destination-types';
import './content-central-sheet.css';

export interface ContentCentralWaitingProps {
  bridge: PaigeDestinationsBridge;
  destinationId: string;
  /** Opens the matching on that type (Review matching). */
  onReview: (paigeType: string) => void;
}

/** "invoice", "invoices": the type's name in the sentence; "documents" when it has none. */
function nounOf(type: string | null, count: number): string {
  if (!type) return count === 1 ? 'document' : 'documents';
  const lower = type.toLowerCase();
  return count === 1 ? lower : lower.endsWith('s') ? lower : `${lower}s`;
}

/**
 * The documents waiting here for a match (parity E-34), as CapturePoint says it: "42 credit memos wait here.
 * Credit Memo isn't matched to a Content Central document type yet. Match it to send them; nothing is lost.",
 * with Review matching. They go by themselves once the matching gives the type a place and is saved.
 */
export function ContentCentralWaiting({
  bridge,
  destinationId,
  onReview,
}: ContentCentralWaitingProps) {
  const [waits, setWaits] = useState<PaigeCcWaiting[]>([]);
  useEffect(() => {
    let live = true;
    bridge.ccWaiting?.(destinationId).then(
      (found) => live && setWaits(found),
      () => live && setWaits([]),
    );
    return () => {
      live = false;
    };
  }, [bridge, destinationId]);
  if (waits.length === 0) return null;
  return (
    <div className="paige-cc-waiting" role="status">
      {waits.map((wait) => (
        <WaitLine key={wait.paigeType ?? ''} wait={wait} onReview={onReview} />
      ))}
    </div>
  );
}

function WaitLine({
  wait,
  onReview,
}: {
  wait: PaigeCcWaiting;
  onReview: (paigeType: string) => void;
}) {
  const { paigeType, documents } = wait;
  return (
    <p className="paige-cc-note is-attention">
      <TriangleAlert size={16} aria-hidden="true" />
      <span>
        <strong>
          {documents.toLocaleString()} {nounOf(paigeType, documents)}{' '}
          {documents === 1 ? 'waits' : 'wait'} here.
        </strong>{' '}
        {paigeType ?? 'Their type'} isn&apos;t matched to a Content Central document type yet. Match
        it to send them; nothing is lost.
      </span>
      {paigeType && (
        <button
          type="button"
          className="secondary-button small"
          onClick={() => onReview(paigeType)}
        >
          Review matching
        </button>
      )}
    </p>
  );
}
